/**
 * Symbole przeznaczenia terenu (pole 7.3.4 we wniosku) wraz z kolorami
 * do wyświetlenia na mapie wniosków i w legendzie.
 *
 * Listy symboli są pojedynczym źródłem prawdy — używa ich zarówno formularz
 * wniosku (CreateApplicationForm, sekcja 7.3.4) jak i mapa wniosków
 * (ApplicationsMapPage, legenda + koloryzacja parceli). Dzięki temu kolor
 * na mapie zawsze odpowiada symbolowi wybranemu przez mieszkańca.
 *
 * Źródła:
 *  - Rozporządzenie MRiT z 2023 w sprawie sposobu przygotowania projektu
 *    planu ogólnego gminy (strefy SW/SJ/SZ/SU/SH/SP/SR/SI/SN/SC/SG/SO/SK
 *    + obszary OUZ/OZS/OSD).
 *  - Rozporządzenie Ministra z 2003 w sprawie wymaganego zakresu projektu
 *    MPZP (oznaczenia graficzne: MN/MW/U/UC/RM/RU/P/E/G/W/K/O/C/T/US/ZP/
 *    ZD/ZC/ZN/R/ZL/LD/WS/PG/KDW/KK/KS/KL/KW).
 */

export type SymbolOption = {
  value: string;
  label: string;
};

/** Symbole stref i obszarów Planu Ogólnego Gminy. */
export const PLAN_OGOLNY_SYMBOLS: SymbolOption[] = [
  { value: "SW", label: "SW – strefa wielofunkcyjna z zabudową mieszkaniową wielorodzinną" },
  { value: "SJ", label: "SJ – strefa wielofunkcyjna z zabudową mieszkaniową jednorodzinną" },
  { value: "SZ", label: "SZ – strefa wielofunkcyjna z zabudową zagrodową" },
  { value: "SU", label: "SU – strefa usługowa" },
  { value: "SH", label: "SH – strefa handlu wielkopowierzchniowego" },
  { value: "SP", label: "SP – strefa gospodarcza" },
  { value: "SR", label: "SR – strefa produkcji rolniczej" },
  { value: "SI", label: "SI – strefa infrastrukturalna" },
  { value: "SN", label: "SN – strefa zieleni i rekreacji" },
  { value: "SC", label: "SC – strefa cmentarzy" },
  { value: "SG", label: "SG – strefa górnictwa" },
  { value: "SO", label: "SO – strefa otwarta" },
  { value: "SK", label: "SK – strefa komunikacyjna" },
  { value: "OUZ", label: "OUZ – obszar uzupełnienia zabudowy" },
  { value: "OZS", label: "OZS – obszar zabudowy śródmiejskiej" },
  { value: "OSD", label: "OSD – obszar standardu dostępności infrastruktury społecznej" },
];

/** Symbole terenów MPZP. */
export const MPZP_SYMBOLS: SymbolOption[] = [
  // Tereny zabudowy mieszkaniowej i usługowej
  { value: "MN", label: "MN – teren zabudowy mieszkaniowej jednorodzinnej" },
  { value: "MW", label: "MW – teren zabudowy mieszkaniowej wielorodzinnej" },
  { value: "U", label: "U – teren usług" },
  { value: "UC", label: "UC – teren rozmieszczenia obiektów handlowych >2000 m²" },
  { value: "RM", label: "RM – teren zabudowy zagrodowej" },
  { value: "RU", label: "RU – teren obsługi produkcji w gospodarstwach rolnych" },
  // Tereny infrastruktury i gospodarcze
  { value: "P", label: "P – teren obiektów produkcyjnych, składów i magazynów" },
  { value: "E", label: "E – infrastruktura techniczna – elektroenergetyka" },
  { value: "G", label: "G – infrastruktura techniczna – gazownictwo" },
  { value: "W", label: "W – infrastruktura techniczna – wodociągi" },
  { value: "K", label: "K – infrastruktura techniczna – kanalizacja" },
  { value: "O", label: "O – infrastruktura techniczna – gospodarka odpadami" },
  { value: "C", label: "C – infrastruktura techniczna – ciepłownictwo" },
  { value: "T", label: "T – infrastruktura techniczna – telekomunikacja" },
  // Tereny zieleni i rekreacji
  { value: "US", label: "US – teren sportu i rekreacji" },
  { value: "ZP", label: "ZP – teren zieleni urządzonej" },
  { value: "ZD", label: "ZD – teren ogrodów działkowych" },
  { value: "ZC", label: "ZC – teren cmentarzy" },
  { value: "ZN", label: "ZN – teren zieleni o charakterze naturalnym" },
  // Tereny rolnicze i leśne
  { value: "R", label: "R – teren rolniczy" },
  { value: "ZL", label: "ZL – teren lasu" },
  { value: "LD", label: "LD – teren zadrzewień i zakrzewień" },
  // Tereny wód i inne
  { value: "WS", label: "WS – teren wód powierzchniowych" },
  { value: "PG", label: "PG – teren górnictwa i wydobycia" },
  // Tereny komunikacji
  { value: "KDW", label: "KDW – teren dróg wewnętrznych" },
  { value: "KK", label: "KK – teren komunikacji kolejowej" },
  { value: "KS", label: "KS – teren komunikacji szynowej" },
  { value: "KL", label: "KL – teren komunikacji lotniczej" },
  { value: "KW", label: "KW – teren komunikacji wodnej" },
];

/**
 * Kolor HEX dla każdego symbolu. Klucze są zgodne z `value` z list powyżej.
 * Strategia kolorystyczna:
 *  - Zabudowa mieszkaniowa: czerwienie / róże
 *  - Usługi i handel: niebieskie
 *  - Produkcja i przemysł: szary stalowy / grafitowy
 *  - Rolne: pomarańczowe / żółte
 *  - Zieleń i lasy: zielenie różnych odcieni
 *  - Wody: cyjany / akwamaryny
 *  - Infrastruktura techniczna: mieszana paleta stonowana
 *  - Komunikacja: szarości ciemne i stonowane
 */
export const SYMBOL_COLORS: Record<string, string> = {
  // ───────── Plan Ogólny Gminy — strefy ─────────
  SW: "#c0392b", // wielofunkcyjna z zabudową wielorodzinną
  SJ: "#e74c3c", // wielofunkcyjna z zabudową jednorodzinną
  SZ: "#f39c12", // z zabudową zagrodową
  SU: "#3498db", // usługowa
  SH: "#2980b9", // handlu wielkopowierzchniowego
  SP: "#7f8c8d", // gospodarcza
  SR: "#e67e22", // produkcji rolniczej
  SI: "#bdc3c7", // infrastrukturalna
  SN: "#16a085", // zieleni i rekreacji
  SC: "#2c3e50", // cmentarzy
  SG: "#7b7d7d", // górnictwa
  SO: "#1abc9c", // otwarta
  SK: "#34495e", // komunikacyjna
  // ───────── Plan Ogólny Gminy — obszary uzupełniające ─────────
  OUZ: "#8e44ad", // uzupełnienia zabudowy
  OZS: "#9b59b6", // zabudowy śródmiejskiej
  OSD: "#e91e63", // standardu dostępności infrastruktury społecznej

  // ───────── MPZP — zabudowa mieszkaniowa i usługowa ─────────
  MN: "#ff7979", // mieszkaniowa jednorodzinna
  MW: "#eb4d4b", // mieszkaniowa wielorodzinna
  U: "#3498db", // usługi
  UC: "#2980b9", // handel wielkopowierzchniowy
  RM: "#f0932b", // zabudowa zagrodowa
  RU: "#d35400", // obsługa produkcji rolniczej

  // ───────── MPZP — produkcja i infrastruktura techniczna ─────────
  P: "#636e72", // produkcja, składy, magazyny
  E: "#fdcb6e", // elektroenergetyka
  G: "#d63031", // gazownictwo
  W: "#74b9ff", // wodociągi
  K: "#0984e3", // kanalizacja
  O: "#6c5ce7", // gospodarka odpadami
  C: "#e17055", // ciepłownictwo
  T: "#a29bfe", // telekomunikacja

  // ───────── MPZP — zieleń, rekreacja, rolne, leśne ─────────
  US: "#55efc4", // sport i rekreacja
  ZP: "#00b894", // zieleń urządzona
  ZD: "#81ecec", // ogrody działkowe
  ZC: "#2d3436", // cmentarze
  ZN: "#badc58", // zieleń naturalna
  R: "#fbc531", // rolniczy
  ZL: "#44bd32", // las
  LD: "#4cd137", // zadrzewienia i zakrzewienia

  // ───────── MPZP — wody i górnictwo ─────────
  WS: "#00cec9", // wody powierzchniowe
  PG: "#5d6d7e", // górnictwo i wydobycie

  // ───────── MPZP — komunikacja ─────────
  KDW: "#95a5a6", // drogi wewnętrzne
  KK: "#34495e", // komunikacja kolejowa
  KS: "#576574", // komunikacja szynowa
  KL: "#b2bec3", // komunikacja lotnicza
  KW: "#48dbfb", // komunikacja wodna
};

/** Słownik label-ów symboli (label po spacji / dywizie usunięty — pełny tekst) */
export const SYMBOL_LABELS: Record<string, string> = Object.fromEntries([
  ...PLAN_OGOLNY_SYMBOLS.map((s) => [s.value, s.label] as const),
  ...MPZP_SYMBOLS.map((s) => [s.value, s.label] as const),
]);

/** Fallback kolory dla symboli spoza zdefiniowanych list (np. numerowane
 *  jednostki planistyczne typu "5.4", "1A/U", itd.). */
const FALLBACK_PALETTE = [
  "#ff6b6b",
  "#feca57",
  "#48dbfb",
  "#1dd1a1",
  "#5f27cd",
  "#ff9ff3",
  "#00d2d3",
  "#54a0ff",
  "#ee5253",
  "#c8d6e5",
  "#a4b0be",
  "#ff7f50",
  "#87a96b",
  "#9370db",
  "#20b2aa",
  "#ff1493",
];

/** Prosty deterministyczny hash do wyboru koloru fallback. */
function hashSymbol(symbol: string): number {
  let h = 0;
  for (let i = 0; i < symbol.length; i++) {
    h = (h * 31 + symbol.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/**
 * Zwraca kolor HEX dla danego symbolu przeznaczenia.
 *  - Brak / pusty → jednolity szary `#9ca3af`.
 *  - Symbol zdefiniowany w `SYMBOL_COLORS` → jego kolor.
 *  - W przeciwnym razie deterministyczny kolor z FALLBACK_PALETTE — taki sam
 *    dla identycznego stringa między sesjami.
 */
export function getColorForSymbol(symbol: string | undefined | null): string {
  if (!symbol) return "#9ca3af";
  const trimmed = symbol.trim();
  if (!trimmed) return "#9ca3af";
  const exact = SYMBOL_COLORS[trimmed] || SYMBOL_COLORS[trimmed.toUpperCase()];
  if (exact) return exact;
  return FALLBACK_PALETTE[hashSymbol(trimmed) % FALLBACK_PALETTE.length];
}

/** Zwraca pełny opis symbolu (po dywizie) lub sam symbol, jeśli nie zna. */
export function getSymbolLabel(symbol: string | undefined | null): string {
  if (!symbol) return "";
  const found = SYMBOL_LABELS[symbol.trim()];
  return found || symbol;
}
